import React from 'react';
import styles from '../irlObservations2026.module.css';
import { PriorityMark, RagMark, StatusBadge } from '../chrome';
import { DiscussionPanel } from './IRL_Discussion';
import { DetailHeader, DetailTabs, EvidenceUploadButton, FileRow, MetaPair, ObsCounts, UploadingEvidenceStatus, observationRagLabel, stripHtmlText } from './shared';

const TABS = ['Overview', 'Evidence', 'Related', 'History', 'Discussion'];

export default function Obs_Overview({
    observation,
    tab,
    onTab,
    onBack,
    onCopy,
    copied,
    onRelatedIrl,
    onEdit,
    comment,
    onComment,
    onPostComment,
    mentionCandidates,
    onReply,
    replyFor,
    replyText,
    onReplyText,
    onSubmitReply,
    onEditComment,
    onDeleteComment,
    onCopyComment,
    copiedCommentId,
    onResolveThread,
    onReopenThread,
    focusCommentId,
    onResolve,
    onSupersede,
    onArchive,
    onFollowUp,
    onAnalyse,
    onDismissImpact,
    onOpenFile,
    onUpload,
    uploading,
    analysis,
    onAcceptUpdate,
    onLeaveUnchanged,
    busy,
}) {
    const counts = ObsCounts(observation);
    const canChange = observation.status !== 'Resolved' && observation.status !== 'Superseded' && observation.status !== 'Archived';
    const extraActions = (
        <>
            {canChange ? (
                <button type="button" onClick={onResolve}>
                    Resolve
                </button>
            ) : null}
            {canChange ? (
                <button type="button" onClick={onSupersede}>
                    Supersede
                </button>
            ) : null}
            {observation.status !== 'Archived' ? (
                <button type="button" onClick={onArchive}>
                    Archive
                </button>
            ) : null}
            <button type="button" onClick={onFollowUp}>
                Create follow-up IRL
            </button>
        </>
    );
    return (
        <>
            <DetailHeader
                kind="obs"
                item={observation}
                onBack={onBack}
                onCopy={onCopy}
                copied={copied}
                onEdit={canChange ? onEdit : undefined}
                extraActions={extraActions}
            />
            <DetailTabs tabs={TABS} tab={tab} onTab={onTab} counts={counts} />
            <div className={styles.detailBody}>
                {tab === 'Overview' && (
                    <div className={styles.detailInner}>
                        {observation.evidenceAlert && (
                            <div className={styles.alert}>
                                <p className={styles.alertTitle}>{observation.evidenceAlert.title}</p>
                                <p className={styles.alertBody}>{observation.evidenceAlert.body}</p>
                                <div className={styles.alertActions}>
                                    <button type="button" className={styles.alertLink} onClick={() => onTab('Evidence')}>
                                        Review evidence
                                    </button>
                                    <button type="button" className={styles.alertLink} onClick={onAnalyse} disabled={busy}>
                                        Analyse update →
                                    </button>
                                    <button type="button" className={styles.alertLink} onClick={onDismissImpact} disabled={busy}>
                                        Dismiss impact
                                    </button>
                                </div>
                            </div>
                        )}
                        {analysis && (
                            <div className={styles.alert}>
                                <p className={styles.alertTitle}>{analysis.recommendation.replace(/_/g, ' ')}</p>
                                {analysis.reason ? <p className={styles.alertBody}>{analysis.reason}</p> : null}
                                {analysis.proposed_observation ? <p className={styles.responseBody}>{analysis.proposed_observation}</p> : null}
                                <div className={styles.alertActions}>
                                    {analysis.recommendation === 'UPDATE_RECOMMENDED' ? (
                                        <button type="button" className={styles.alertLink} onClick={onAcceptUpdate} disabled={busy}>
                                            Accept update
                                        </button>
                                    ) : null}
                                    {analysis.recommendation === 'POTENTIALLY_RESOLVED' ? (
                                        <button type="button" className={styles.alertLink} onClick={onResolve} disabled={busy}>
                                            Resolve
                                        </button>
                                    ) : null}
                                    {analysis.recommendation === 'POTENTIALLY_SUPERSEDED' ? (
                                        <button type="button" className={styles.alertLink} onClick={onSupersede} disabled={busy}>
                                            Supersede
                                        </button>
                                    ) : null}
                                    <button type="button" className={styles.alertLink} onClick={onLeaveUnchanged}>
                                        Leave unchanged
                                    </button>
                                </div>
                            </div>
                        )}
                        {observation.supersededBy ? (
                            <p className={styles.emptyBody}>Superseded by {observation.supersededBy}</p>
                        ) : null}
                        <div className={styles.metaGrid}>
                            <MetaPair label="Workstream">{observation.workstreamName}</MetaPair>
                            <MetaPair label="Materiality">
                                <PriorityMark value={observation.materiality} />
                            </MetaPair>
                            <MetaPair label="RAG">
                                <RagMark value={observationRagLabel(observation)} />
                            </MetaPair>
                            <MetaPair label="Status">
                                <StatusBadge status={observation.status} />
                            </MetaPair>
                            <MetaPair label="Confirmed by">{observation.confirmedBy || '—'}</MetaPair>
                            <MetaPair label="Confirmed on">{observation.confirmedOn || '—'}</MetaPair>
                            <MetaPair label="Updated">{observation.updated}</MetaPair>
                        </div>
                        <div className={styles.sectionLabelSm}>Observation</div>
                        <div className={styles.card}>
                            <p className={styles.responseBody}>{stripHtmlText(observation.body)}</p>
                        </div>
                        {observation.why ? (
                            <>
                                <div className={`${styles.sectionLabelSm} ${styles.sectionGap}`}>Why it matters</div>
                                <p className={styles.responseBody}>{stripHtmlText(observation.why)}</p>
                            </>
                        ) : null}
                        {observation.revisions?.length > 0 && (
                            <>
                                <div className={`${styles.sectionLabelSm} ${styles.sectionGap}`}>
                                    Revisions
                                </div>
                                {observation.revisions.map(item => (
                                    <div key={item.version} className={styles.revisionCard}>
                                        <div className={styles.revisionHead}>
                                            <span className={styles.responseTitle}>
                                                Revision {item.version}
                                                {item.current ? ' · Current' : ''}
                                            </span>
                                            <span className={styles.metaPlain}>
                                                {item.date} · {item.by}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                                <button type="button" className={styles.outlineBtn} onClick={onFollowUp}>
                                    Create follow-up IRL
                                </button>
                            </>
                        )}
                    </div>
                )}
                {tab === 'Evidence' && (
                    <div className={styles.wideInner} aria-busy={uploading ? 'true' : undefined}>
                        <div className={styles.sectionRow}>
                            <div className={styles.sectionLabelSm}>Supporting evidence</div>
                            <EvidenceUploadButton onUpload={onUpload} disabled={busy || uploading} />
                        </div>
                        {uploading ? <UploadingEvidenceStatus /> : null}
                        {(observation.evidence || []).length === 0 && !uploading ? (
                            <div className={styles.emptyResponse}>
                                <p className={styles.emptyTitle}>No evidence linked</p>
                                <p className={styles.emptyBody}>Documents linked to this observation will appear here.</p>
                            </div>
                        ) : (
                            (observation.evidence || []).map(file => (
                                <FileRow key={file.documentId || file.name} file={file} onOpen={onOpenFile} />
                            ))
                        )}
                    </div>
                )}
                {tab === 'Related' && (
                    <div className={styles.detailInner}>
                        <div className={styles.sectionLabelSm}>Related IRLs</div>
                        {(observation.relatedIrls || []).length === 0 && <p className={styles.emptyBody}>No related requests.</p>}
                        {(observation.relatedIrls || []).map(item => (
                            <button key={item.id} type="button" className={styles.relatedCard} onClick={() => onRelatedIrl?.(item.id)}>
                                <span>
                                    <span className={styles.rowId}>{item.id}</span>
                                    <span className={styles.rowTitle}>{stripHtmlText(item.title)}</span>
                                </span>
                            </button>
                        ))}
                    </div>
                )}
                {tab === 'History' && (
                    <div className={styles.detailInner}>
                        {(observation.history || []).map(item => (
                            <div key={`${item.date}-${item.title}`} className={styles.historyItem}>
                                <div className={styles.historyDate}>{item.date}</div>
                                <div>
                                    <div className={styles.historyTitle}>{item.title}</div>
                                    <div className={styles.historyDetail}>{item.detail}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {tab === 'Discussion' && (
                    <DiscussionPanel
                        item={observation}
                        comment={comment}
                        onComment={onComment}
                        onPostComment={onPostComment}
                        mentionCandidates={mentionCandidates}
                        onReply={onReply}
                        replyFor={replyFor}
                        replyText={replyText}
                        onReplyText={onReplyText}
                        onSubmitReply={onSubmitReply}
                        onEditComment={onEditComment}
                        onDeleteComment={onDeleteComment}
                        onCopyComment={onCopyComment}
                        copiedCommentId={copiedCommentId}
                        onResolveThread={onResolveThread}
                        onReopenThread={onReopenThread}
                        focusCommentId={focusCommentId}
                        commentPrefix="obs-comment"
                        busy={busy}
                    />
                )}
            </div>
        </>
    );
}
